import { Activity, Ear, LogOut, MoreHorizontal, PhoneIncoming, RefreshCw, Users } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { SummaryTile } from "@/features/call-report";
import { stateText } from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  STATS_WINDOWS, callKind, callParty, callersInOrder, callsOnLine, canMonitor, canSignOut,
  currentLine, lineTitle, longestWait, meantForText, onACall, percent, repCounts, repName,
  repParty, repsOnLine, secondsSince, served, windowStart, type CallParty, type StatsWindow,
} from "@/lib/floor";
import { askingToMonitor, listenInsOnPhone, rememberMonitor } from "@/lib/monitor";
import { useAcdFloor, useAcdStats, useSignOutRep, useStartMonitor } from "@/lib/queries";
import type { AcdFloor, FloorLine, FloorRep, LineStats, WaitingCaller } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDuration, formatPhoneNumber } from "@/lib/utils";
import { useMonitors } from "@/softphone/acd-call";
import { AVAILABILITY_DOT } from "@/softphone/availability";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * The live floor: who is on the phones, who is waiting, and how a line has done — tap-ucaas's
 * queue monitor, over this platform's ACD (ADR-018).
 *
 * One line at a time, as in tap-ucaas. A supervisor watching a line wants that line's numbers side
 * by side, not every line's squeezed into columns, and the line picked stays picked while callers
 * come and go (`currentLine`).
 *
 * Two speeds. What is happening now comes from the event-tracker's live records (`GET /acd/floor`),
 * refetched on the `queue` hint as reps and callers move and every 10 seconds besides, with waits
 * and times in state counting up on this page's clock between refetches. How the line has done
 * comes from the queue history (`GET /acd/stats`), which moves slowly and polls every 30 seconds
 * rather than riding the hints. A rep's state reads in the phone's own words (`stateText`), so a
 * supervisor and a rep never describe the same state two ways.
 */
export function FloorPage() {
  const floor = useAcdFloor();
  const [picked, setPicked] = React.useState<string | null>(null);
  const now = useNow(1_000);
  const lines = floor.data?.lines ?? [];
  const name = currentLine(lines, picked);
  const line = lines.find((l) => l.name === name) ?? null;

  return (
    <Page>
      <PageHeader
        title="Live floor"
        description="Who is on the phones and who is waiting, one line at a time, with how the line has done."
        action={
          <Button
            variant="outline"
            disabled={floor.isFetching}
            onClick={() => void floor.refetch()}
          >
            <RefreshCw className={cn(floor.isFetching && "animate-spin")} />
            Refresh
          </Button>
        }
      />

      {floor.isPending ? (
        <Card>
          <TableSkeleton cols={5} />
        </Card>
      ) : !floor.data ? (
        <Card>
          <ErrorState
            error={floor.error}
            title="Could not load the floor"
            action={
              <Button variant="outline" onClick={() => void floor.refetch()}>
                Try again
              </Button>
            }
          />
        </Card>
      ) : line === null ? (
        <Card>
          <EmptyState
            icon={Activity}
            title="No lines to watch"
            description="A campaign's lines appear here once it has one enabled."
            action={
              <Button variant="outline" asChild>
                <Link to="/campaigns">Go to campaigns</Link>
              </Button>
            }
          />
        </Card>
      ) : (
        <FloorLineView
          floor={floor.data}
          line={line}
          now={now}
          stale={floor.error !== null}
          onPick={setPicked}
        />
      )}
    </Page>
  );
}

function FloorLineView({
  floor, line, now, stale, onPick,
}: {
  floor: AcdFloor;
  line: FloorLine;
  now: number;
  /** The last refetch failed, and this is the floor as it was last read. */
  stale: boolean;
  onPick: (name: string) => void;
}) {
  const reps = repsOnLine(floor.reps, line.name);
  const counts = repCounts(reps, line.name);
  const names = new Map(floor.reps.map((rep) => [`${rep.user}@${rep.domain}`, repName(rep)]));
  const callers = callersInOrder(line.callers);
  const longest = longestWait(callers, now);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={line.name} onValueChange={onPick}>
          <SelectTrigger className="w-80 max-w-full" aria-label="Line">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {floor.lines.map((l) => (
              <SelectItem
                key={l.name}
                value={l.name}
                description={l.waiting > 0 ? `${l.waiting} waiting` : undefined}
              >
                {lineTitle(l)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {stale && (
          <span className="text-xs text-warning-11">
            Could not refresh the floor. This is what it last read.
          </span>
        )}
      </div>

      <section className="space-y-2" aria-labelledby="floor-now">
        <h2 id="floor-now" className="text-sm font-semibold text-text">
          Now
        </h2>
        {/* Seven tiles fit one row on a wide screen. With My calls only's eighth, two rows of
            four, as the line's history below has, rather than one tile alone on a second row. */}
        <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-4", counts.mine === 0 && "xl:grid-cols-7")}>
          <SummaryTile
            label="Waiting"
            value={callers.length}
            hint={longest === null ? "Nobody waiting" : `Longest ${formatDuration(longest)}`}
          />
          <SummaryTile label="Signed in" value={counts.signedIn} />
          <SummaryTile label="Ready" value={counts.ready} />
          {counts.mine > 0 && (
            <SummaryTile
              label="My calls only"
              value={counts.mine}
              hint="Free, but only for their own callers"
            />
          )}
          <SummaryTile label="On a call" value={counts.onCall} />
          <SummaryTile label="Wrapping up" value={counts.wrappingUp} />
          <SummaryTile label="On a break" value={counts.onBreak} />
          <SummaryTile
            label="Handoffs pending"
            value={floor.handoffs_pending}
            hint="Across every line"
          />
        </div>
      </section>

      {/* What is happening now comes before how the line has done. The reps take the width,
          with the callers waiting as a narrow queue beside them where the page is wide enough
          for the reps' table to keep each row on its lines, and under them where it is not. */}
      <div className="@container">
        <div className="grid gap-4 @min-[68rem]:grid-cols-[minmax(0,1fr)_21rem]">
          <RepsCard line={line.name} reps={reps} now={now} />
          <CallersCard callers={callers} names={names} now={now} />
        </div>
      </div>

      <LineHistory line={line.name} now={now} />
    </div>
  );
}

/** The line's numbers over a window, from the queue history. The window scopes only these, so its
 *  control sits above them. */
function LineHistory({ line, now }: { line: string; now: number }) {
  const [range, setRange] = React.useState<StatsWindow>("today");
  // On the minute, so the query's key changes once a minute and not on every tick.
  const since = windowStart(range, new Date(Math.floor(now / 60_000) * 60_000));
  const stats = useAcdStats({ queue: line, since });
  const totals: LineStats | undefined = stats.data?.totals;
  const seconds = (value: number | null | undefined) =>
    value === null || value === undefined ? "—" : formatDuration(value);

  return (
    <section className="space-y-2" aria-labelledby="floor-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="floor-history" className="text-sm font-semibold text-text">
          How the line has done
        </h2>
        <div className="flex items-center gap-2">
          {stats.isFetching && <Spinner className="size-3.5" />}
          <Select value={range} onValueChange={(next) => setRange(next as StatsWindow)}>
            <SelectTrigger className="w-40" aria-label="Counted over">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {STATS_WINDOWS.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {stats.error && !totals ? (
        <Callout tone="danger">
          Could not count the line's history:{" "}
          {stats.error instanceof ApiError ? stats.error.message : "please try again."}
        </Callout>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryTile label="Offered" value={totals?.offered} loading={!totals} />
          <SummaryTile
            label="Answered"
            value={totals ? served(totals) : undefined}
            hint={totals ? `${percent(totals.answer_rate)} answer rate` : undefined}
            loading={!totals}
          />
          <SummaryTile
            label="Abandoned"
            value={totals?.abandoned}
            hint={totals ? `${percent(totals.abandon_rate)} abandon rate` : undefined}
            loading={!totals}
          />
          <SummaryTile
            label="Service level"
            value={percent(totals?.service_level)}
            hint={stats.data ? `Answered within ${stats.data.sla_seconds} s` : undefined}
            loading={!totals}
          />
          <SummaryTile
            label="Average wait"
            value={seconds(totals?.wait_avg)}
            hint={totals?.wait_p90 != null ? `90% within ${formatDuration(totals.wait_p90)}` : undefined}
            loading={!totals}
          />
          <SummaryTile label="Average ring" value={seconds(totals?.ring_avg)} loading={!totals} />
          <SummaryTile
            label="Rang out"
            value={totals?.ronas}
            hint="Offers not picked up"
            loading={!totals}
          />
          <SummaryTile label="Average talk" value={seconds(totals?.talk_avg)} loading={!totals} />
        </div>
      )}
    </section>
  );
}

/**
 * Listening in on a rep's call from the supervisor's own phone (ADR-029). The ACD rings that phone
 * into the call's room, muted, and the phone answers by itself, without a ring, and offers whisper
 * and barge (`rememberMonitor`), so the phone has to be connected in this tab. `blocked` says why
 * it cannot.
 */
function useListenIn() {
  const { user } = useAuth();
  const phone = useSoftphone();
  const toast = useToast();
  const start = useStartMonitor();
  const monitors = useMonitors();
  // The rep's call a Listen is leaving the current listen-in for, while that hang-up goes through.
  const [leaving, setLeaving] = React.useState<string | null>(null);
  const onPhone = listenInsOnPhone(monitors, phone.calls);
  const me =
    user?.sip_endpoint_extension && user.sip_endpoint_domain_name
      ? { user: user.sip_endpoint_extension, domain: user.sip_endpoint_domain_name }
      : null;
  const blocked = !user?.sip_endpoint_id
    ? "Listening in on a call needs a phone of your own, which an admin assigns."
    : phone.registration !== "registered"
      ? "Connect your phone in this tab to listen in on a call."
      : null;
  return {
    me,
    blocked,
    pendingFor: leaving ?? (start.isPending ? (start.variables ?? null) : null),
    /** The calls this supervisor is listening in on now: their Listen is dimmed. */
    listening: new Set(onPhone.flatMap((m) => (m.callId ? [m.callId] : []))),
    start: async (rep: FloorRep) => {
      const callId = rep.callerChannelId;
      if (!callId) return;
      // The phone stays quiet until the answer names the call it rings with (`askingToMonitor`).
      const answered = askingToMonitor();
      // One call at a time: the listen-in the supervisor is on is hung up first, then the new one
      // is asked for. A client's call they are on is theirs to end; it is not touched here.
      if (onPhone.length > 0) {
        setLeaving(callId);
        for (const m of onPhone) {
          await phone.hangup(m.monitorId).catch((e: unknown) => {
            console.error("[floor] could not leave the listen-in", e);
          });
        }
        setLeaving(null);
      }
      start.mutate(callId, {
        onSuccess: (res) => {
          rememberMonitor(res.monitor_id, repName(rep), callId);
          answered();
          toast.success("Listening in", `Your phone joins ${repName(rep)}'s call. Nobody hears you.`);
        },
        onError: (e) => {
          answered();
          toast.error("Could not listen in", e instanceof ApiError ? e.message : "Please try again.");
        },
      });
    },
  };
}

function RepsCard({ line, reps, now }: { line: string; reps: FloorRep[]; now: number }) {
  const listen = useListenIn();
  const toast = useToast();
  const signOut = useSignOutRep();
  const [signingOut, setSigningOut] = React.useState<FloorRep | null>(null);
  const blocked = listen.blocked !== null && reps.some((rep) => canMonitor(rep, listen.me));
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Reps"
        description={`A rep's state is the one across all their lines. Handled counts the calls they finished on this line since signing in to it.${blocked ? ` ${listen.blocked}` : ""}`}
      />
      {reps.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nobody is signed in to this line"
          description="Reps sign in with Ready on their phone."
        />
      ) : (
        // Every cell is two lines at most and none of them wraps: a row that grows a line whenever
        // a name is long reads as a different kind of row. Long names are cut short, whole in
        // their tooltip.
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rep</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Call</TableHead>
              <TableHead className="text-right">Handled</TableHead>
              <TableHead className="w-0">
                <span className="sr-only">Listen in, or sign the rep out</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reps.map((rep) => {
              const view = stateText(rep.state, rep.code);
              const inState = secondsSince(rep.since, now);
              const mine = (rep.ownCallsOnly ?? []).includes(line);
              return (
                <TableRow key={`${rep.user}@${rep.domain}`}>
                  <TableCell className="py-2">
                    <span className="block max-w-48 truncate text-sm font-medium text-text" title={repName(rep)}>
                      {repName(rep)}
                    </span>
                    <span className="block font-mono text-xs text-text-muted">{rep.user}</span>
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-text">
                      <span className={cn("size-2 shrink-0 rounded-full", AVAILABILITY_DOT[view.tone])} aria-hidden />
                      {view.label}
                    </span>
                    {/* Under the state and in line with its words: how long the rep has been in it. */}
                    <span className="flex items-center gap-1.5 whitespace-nowrap pl-3.5 text-xs text-text-muted">
                      {inState !== null && (
                        <span>
                          for <span className="font-mono tabular-nums">{formatDuration(inState)}</span>
                        </span>
                      )}
                      {mine && (
                        <Badge tone="outline" size="sm">
                          My calls only
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    <RepCall rep={rep} />
                  </TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{callsOnLine(rep, line) ?? "—"}</TableCell>
                  <TableCell className="py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {canMonitor(rep, listen.me) &&
                        (rep.callerChannelId && listen.listening.has(rep.callerChannelId) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                            aria-label={`You are listening in on ${repName(rep)}'s call`}
                          >
                            <Ear />
                            Listening
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={listen.blocked !== null}
                            loading={listen.pendingFor === rep.callerChannelId}
                            aria-label={`Listen in on ${repName(rep)}'s call`}
                            onClick={() => void listen.start(rep)}
                          >
                            <Ear />
                            Listen
                          </Button>
                        ))}
                      {/* Signing a rep out is rare (the rep whose phone is long gone), so it waits
                          in the row's menu rather than sitting beside Listen on every row. */}
                      {canSignOut(rep, listen.me) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${repName(rep)}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              destructive
                              aria-label={`Sign ${repName(rep)} out of the ACD`}
                              onSelect={() => setSigningOut(rep)}
                            >
                              <LogOut />
                              Sign out
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* The floor's own Sign out (ADR-052 in `platform`): for the rep whose phone is long gone,
          and for the one who went home without signing out. */}
      <ConfirmDialog
        open={signingOut !== null}
        onOpenChange={(open) => !open && setSigningOut(null)}
        title="Sign this rep out?"
        description={
          <>
            <span className="font-medium">{signingOut ? repName(signingOut) : ""}</span> comes off
            every line they are signed in to and leaves the floor. They take calls again when they
            press Ready on their own phone.
            {signingOut !== null && onACall(signingOut) && (
              <span className="block pt-2">
                They are on a call: the ACD signs them out when it ends, never mid-call.
              </span>
            )}
          </>
        }
        confirmLabel="Sign out"
        destructive
        loading={signOut.isPending}
        onConfirm={async () => {
          if (signingOut === null) return;
          const name = repName(signingOut);
          try {
            await signOut.mutateAsync({ user: signingOut.user, domain: signingOut.domain });
            setSigningOut(null);
            toast.success("Signed out", `${name} comes off their lines.`);
          } catch (e) {
            toast.error(
              "Could not sign them out",
              e instanceof ApiError ? e.message : "Please try again.",
            );
          }
        }}
      />
    </Card>
  );
}

/** Who a rep is on the phone with, and what kind of call it is: the contact on a line's call in
 *  or out, the lead the dialer is calling for them, or whoever is on a call of their own. */
function RepCall({ rep }: { rep: FloorRep }) {
  const party = repParty(rep);
  if (party === null) return <span className="text-xs text-text-muted">—</span>;
  const kind = rep.state === "ringing" || rep.state === "busy" ? callKind(rep.kind) : null;
  if (party.name === null && party.number === null) {
    return <span className="text-xs text-text-muted">{kind ?? "A call"}</span>;
  }
  return <PartyText party={party} detail={kind} />;
}

/** A caller by name, linked to their contact, over their number; by number alone when nobody
 *  knows their name. Two lines, and a long name is cut short. */
function PartyText({
  party, detail, wrap = false,
}: {
  party: CallParty;
  /** Said after the number on the second line: the call's kind, how the line holds them. */
  detail?: React.ReactNode;
  /** The second line may go onto a third, in a column too narrow for it (the waiting queue). */
  wrap?: boolean;
}) {
  const number = party.number ? formatPhoneNumber(party.number) : null;
  const title = party.name ?? number ?? "Unknown caller";
  const shownNumber = party.name !== null ? number : null;
  return (
    <>
      <span className="block max-w-56 truncate text-sm text-text" title={title}>
        {party.leadId ? (
          <Link to={`/contacts/${party.leadId}`} className="hover:underline">
            {title}
          </Link>
        ) : (
          title
        )}
      </span>
      {(shownNumber || detail) && (
        <span
          className={cn(
            "flex items-center gap-x-1.5 text-xs text-text-muted",
            wrap ? "flex-wrap gap-y-0.5" : "whitespace-nowrap",
          )}
        >
          {shownNumber && <span className="whitespace-nowrap font-mono">{shownNumber}</span>}
          {shownNumber && detail && <span aria-hidden>·</span>}
          {detail}
        </span>
      )}
    </>
  );
}

function CallersCard({
  callers, names, now,
}: {
  callers: WaitingCaller[];
  /** `user@domain` → the rep's name, for a caller who dialed a rep's own number. */
  names: ReadonlyMap<string, string>;
  now: number;
}) {
  // A queue, not a table: it sits in a narrow column beside the reps, where five columns would
  // wrap, and each caller reads as one entry — who, what kind of call, how long they have waited.
  return (
    <Card className="overflow-hidden">
      <CardHeader title="Waiting" description="In the order the line serves them, with how long each has waited." />
      {callers.length === 0 ? (
        <EmptyState
          icon={PhoneIncoming}
          title="Nobody is waiting"
          description="A caller appears here from the moment they join the line."
        />
      ) : (
        <ol className="divide-y divide-border">
          {callers.map((caller, i) => {
            const waited = secondsSince(caller.joinedAt, now);
            const kind = callKind(caller.kind);
            const meantFor = meantForText(caller, names, now);
            // 0 is the ACD's default priority, so only another is worth a word.
            const priority = caller.priority !== null && caller.priority !== undefined && caller.priority !== 0
              ? caller.priority
              : null;
            return (
              <li key={caller.channelId} className="flex items-start gap-3 px-4 py-2.5">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded bg-gray-3 text-2xs font-medium tabular-nums text-gray-11">
                  {caller.position ?? i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <PartyText
                    party={callParty(caller)}
                    wrap
                    detail={
                      kind || priority !== null ? (
                        <>
                          {kind && (
                            <Badge tone={caller.kind === "handoff" ? "accent" : "neutral"} size="sm">
                              {kind}
                            </Badge>
                          )}
                          {priority !== null && <span>Priority {priority}</span>}
                        </>
                      ) : null
                    }
                  />
                  {meantFor && <span className="block text-xs text-accent-11">{meantFor}</span>}
                </div>
                <span className="mt-0.5 shrink-0 font-mono text-xs tabular-nums text-text">
                  {waited === null ? "—" : formatDuration(waited)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
